// EP-135 Tier 3 — Customer-facing meeting list (replaces the Tier 1a
// "coming soon" placeholder).
//
// The list shows meetings where the authenticated customer is either an
// attendee OR a proxy holder (§12 S-4). Tapping a row navigates to the
// meeting detail (ballot entry) at /meetings/:id.
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CalendarDaysIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useMeetings } from '@/hooks/useMeetings'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { MeetingStatus } from '@/types/meetings'

const STATUS_BADGE: Record<MeetingStatus, string> = {
  draft: 'status-badge-ghost',
  noticed: 'status-badge-info',
  in_progress: 'status-badge-warning',
  voting_closed: 'status-badge-info',
  certified: 'status-badge-success',
}

export default function MeetingsPage() {
  const { t } = useTranslation('meetings')
  const { t: tc } = useTranslation('common')
  const { formatDateTime } = useDateFormatter()
  const { data: meetings, isLoading, error } = useMeetings()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-4 px-4 pt-4">
      <div className="flex items-center gap-3">
        <div className="icon-container">
          <CalendarDaysIcon className="w-5 h-5" />
        </div>
        <h1 className="text-lg font-bold text-base-content">
          {t('title')}
        </h1>
      </div>

      {meetings && meetings.length === 0 ? (
        <div className="flat-card p-6 text-center space-y-2">
          <p className="text-sm font-medium text-base-content">
            {t('empty_title')}
          </p>
          <p className="text-xs text-base-content/60 leading-relaxed">
            {t('empty_body')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {(meetings ?? []).map((meeting) => (
            <Link
              key={meeting.id}
              to={`/meetings/${meeting.id}`}
              className="flat-card block"
              data-testid={`meeting-row-${meeting.id}`}
            >
              <div className="list-item-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm truncate">
                      {meeting.title}
                    </p>
                    <span className={STATUS_BADGE[meeting.status]}>
                      {t(`status.${meeting.status}`)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-xs text-base-content/60">
                      {formatDateTime(meeting.meeting_at)}
                    </p>
                    {meeting.call_number > 1 && (
                      <span className="text-[10px] text-base-content/50">
                        {t('call_n', { n: meeting.call_number })}
                      </span>
                    )}
                  </div>
                  {(meeting.proxy_for.length > 0 || !meeting.own_attendee) && (
                    <p className="text-[11px] text-base-content/50 mt-1">
                      {!meeting.own_attendee
                        ? t('proxy_role_only')
                        : meeting.proxy_for.length === 1
                          ? t('proxy_for_one', {
                              name: meeting.proxy_for[0].owner_customer_name,
                            })
                          : t('proxy_for_other', {
                              count: meeting.proxy_for.length,
                            })}
                    </p>
                  )}
                </div>
                <ChevronRightIcon className="w-4 h-4 text-base-content/40 shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
